import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import { FaMapMarkerAlt, FaTasks, FaBuilding, FaMap, FaArrowRight, FaExpand, FaCompress, FaTimes, FaExpandArrowsAlt, FaExclamationTriangle, FaWrench, FaGlobe, FaSearchLocation } from "react-icons/fa";
import Map from '../../../components/Map/Map'; 
import { API } from 'aws-amplify';
import SidePanel from '../SidePanel/SidePanel';
import SidePanelDet from '../SitePanelDet/SidePanelDet';
import Graph from '../../../components/Graph';
import PageHeader from '../../../components/PageHeader/PageHeader';
import '../../../components/PageHeader/PageHeader.css';
import { getChartColors, getMapView } from '../../../theme/palette';
import { mapColorScheme, useDisplayMode } from '../../../theme/displayMode';
import { headerLogo } from '../../../theme/brandAssets';
import { useI18n } from '../../../i18n';

import "./Dashboard.css";

const PIN_PATH = 'M14 2C7.925 2 3 6.925 3 13c0 8.4 11 20.2 11 20.2S25 21.4 25 13C25 6.925 20.075 2 14 2z';
const PIN_COLORS = {
  created: '#C47B12',
  accepted: '#D64545',
  started: '#1769AA',
  wip: '#0E7C86',
  completed: '#278653',
};
const PIN_Z = {
  completed: 10,
  created: 20,
  accepted: 30,
  started: 40,
  wip: 50,
};

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function splitStamp(value) {
  const stamp = String(value || '').trim();
  const space = stamp.indexOf(' ');
  if (space < 0) return { day: stamp, time: '' };
  return { day: stamp.slice(0, space), time: stamp.slice(space + 1) };
}

function pinStatusKey(status) {
  const s = (status || '').toLowerCase();
  if (s === 'accepted') return 'accepted';
  if (s === 'started') return 'started';
  if (s === 'completed') return 'completed';
  if (s === 'wip') return 'wip';
  return 'created';
}

function pinNode(status) {
  const key = PIN_COLORS[status] ? status : 'created';
  const wrap = document.createElement('div');
  wrap.className = `dash-pin is-${key}`;
  wrap.innerHTML =
    `<svg viewBox="0 0 28 36" width="26" height="34" aria-hidden="true">` +
      `<path d="${PIN_PATH}"/>` +
      `<circle cx="14" cy="13" r="4.2"/>` +
    `</svg>`;
  return wrap;
}

function clip(value, max) {
  const text = String(value || '');
  return text.length > max ? text.slice(0, max) + '…' : text;
}

function markerInfoHtml(task, status) {
  const key = PIN_COLORS[status] ? status : 'created';
  const site = escapeHtml(task.site_ref || 'Site');
  const address = escapeHtml(clip(task.site_address, 48));
  const ref = escapeHtml(task.task_ref || '');
  const fault = escapeHtml(clip(task.fault_description, 90));
  const by = escapeHtml(task.user_created_str || '—');
  const contractor = escapeHtml((task.contractor_str || '').replace(/\s[-–]\s*$/, ''));
  const statusLabel = escapeHtml(task.task_status || key);
  const when = escapeHtml(task.date_upd || '');
  const urgent = task.urgent_fault && task.urgent_fault !== '-'
    ? escapeHtml(task.urgent_fault)
    : '';
  const href = '/task_det/' + encodeURIComponent(task.taskId || '');
  const meta = [ref, fault].filter(Boolean).join(' · ');
  const who = [by !== '—' ? 'Created by ' + by : '', when].filter(Boolean).join(' · ');

  return (
    '<div class="dash-iw">' +
      '<div class="dash-iw-head">' +
          '<span class="dash-iw-dot is-' + key + '"></span>' +
        '<strong>' + site + '</strong>' +
      '</div>' +
      (address ? '<p class="dash-iw-address">' + address + '</p>' : '') +
      (meta ? '<p class="dash-iw-meta">' + meta + '</p>' : '') +
      (who ? '<p class="dash-iw-meta">' + who + '</p>' : '') +
      (contractor ? '<p class="dash-iw-meta">' + contractor + '</p>' : '') +
      '<div class="dash-iw-foot">' +
        '<span class="dash-iw-status is-' + key + '">' +
          statusLabel + (urgent ? ' · ' + urgent : '') +
        '</span>' +
        '<a class="dash-iw-open" href="' + href + '">Open</a>' +
      '</div>' +
    '</div>'
  );
}

function formatClock(date) {
  if (!date) return '—';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function isSlaOverdue(task, now = Date.now()) {
  if (!task || task.date_completed) return false;
  const sla = parseFloat(task.sla_attend);
  if (!Number.isFinite(sla) || sla <= 0 || !task.date_created) return false;
  const created = new Date(task.date_created).getTime();
  if (!Number.isFinite(created)) return false;
  const unit = task.category === 'PPM' || /day/i.test(String(task.sla_attend_in || ''))
    ? 86400000
    : 3600000;
  return now > created + sla * unit;
}

function LegendPin({ status, label, count, hidden, onToggle, showTitle, hideTitle }) {
  return (
    <button
      type="button"
      className={`map-legend-item${hidden ? ' is-off' : ''}`}
      onClick={onToggle}
      aria-pressed={!hidden}
      title={hidden ? showTitle : hideTitle}
    >
      <span className={`dash-legend-pin is-${status}`} aria-hidden="true">
        <svg viewBox="0 0 28 36">
          <path d={PIN_PATH} />
          <circle cx="14" cy="13" r="4.2" />
        </svg>
      </span>
      {label} <span className="map-legend-count">{count}</span>
    </button>
  );
}

function addMarkers(map, links) {
  if (!map || !window.google) return undefined;
  const markers = [];
  let infoWindow;
  let cancelled = false;

  const tearDown = () => {
    cancelled = true;
    if (infoWindow) infoWindow.close();
    markers.forEach((item) => { item.marker.map = null; });
    markers.length = 0;
  };

  const place = () => {
    if (cancelled || !window.google.maps.marker) return;
    const clearSelection = () => {
      markers.forEach((item) => {
        item.el.classList.remove('is-selected');
        item.marker.zIndex = PIN_Z[item.status] || 10;
      });
    };

    (links || []).forEach((link) => {
      if (!link || !link.coords) return;
      const lat = Number(link.coords.lat);
      const lng = Number(link.coords.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const status = pinStatusKey(link.status);
      const el = pinNode(status);
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat, lng },
        title: link.title || '',
        content: el,
        zIndex: PIN_Z[status] || 10,
      });
      marker.addListener('click', () => {
        clearSelection();
        el.classList.add('is-selected');
        marker.zIndex = 80;
        if (infoWindow) infoWindow.close();
        infoWindow = new window.google.maps.InfoWindow({
          content: link.content,
          pixelOffset: new window.google.maps.Size(0, -8),
        });
        infoWindow.open(map, marker);
      });
      markers.push({ marker, el, status });
    });
  };

  if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
    place();
  } else {
    window.google.maps.importLibrary('marker').then(place);
  }

  return tearDown;
}

const Dashboard = (props) => {
  const { t, language, tStatus, tCatalog } = useI18n();
  const chartCatLabel = (key) => {
    if (key === 'Reactive') return t('dashboard.chartReactive');
    if (key === 'PPM Inspections') return t('dashboard.chartPpmInspections');
    if (key === 'PPM Routine Maint') return t('dashboard.chartPpmRoutine');
    return tCatalog(key);
  };
  const { theme, viewMode, setViewMode } = useDisplayMode();
  const chartColors = getChartColors(theme);
  const completedSeries = {
    backgroundColor: chartColors.completed,
    hoverBackgroundColor: chartColors.completedHover,
    borderColor: chartColors.completedBorder,
    borderWidth: 1,
  };
  const activeSeries = {
    backgroundColor: chartColors.active,
    hoverBackgroundColor: chartColors.activeHover,
    borderColor: chartColors.activeBorder,
    borderWidth: 1,
  };
  const isWall = viewMode === 'wall';
  const [mapExpanded, setMapExpanded] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [dataUpdatedAt, setDataUpdatedAt] = useState(null);
  const [dataError, setDataError] = useState(false);
  const [hiddenPins, setHiddenPins] = useState({});
  const [urgentUnassigned, setUrgentUnassigned] = useState(0);
  const [urgentAged, setUrgentAged] = useState(0);
  const [outstandingSlaOverdue, setOutstandingSlaOverdue] = useState(0);
  const [slaOverdue, setSlaOverdue] = useState(0);
  const [mapFrame, setMapFrame] = useState('sites');
  const [mapFrameSeq, setMapFrameSeq] = useState(0);
  const [unattended, setUnattended] = useState(false);
  const idleTimerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [serviceCall, setServiceCall] = useState({});
  const [reactiveData, setReactiveData] = useState({});
  const [ppmData, setPpmData] = useState({});
  const [slaAttendanceData, setSlaAttendanceData] = useState({});
  const [slaRepairData, setSlaRepairData] = useState({});
  const [users, setUsers] = useState([]);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapTasks, setMapTasks] = useState([]);
  const [tasks, setTasks] = useState([]);

  //Set Weekly / Monthly information
  const [taskActiveW, setTaskActiveW] = useState(0);
  const [taskActiveD, setTaskActiveD] = useState(0);
  const [taskActiveDet, setTaskActiveDet] = useState([]);

  const [taskCompleteW, setTaskCompleteW] = useState(0);
  const [taskCompleteD, setTaskCompleteD] = useState(0);
  const [taskCompleteDet, setTaskCompleteDet] = useState([]);

  const [taskWipW, setTaskWipW] = useState(0);
  const [taskWipD, setTaskWipD] = useState(0);
  const [taskWipDet, setTaskWipDet] = useState([]);
  const [taskWipOpen, setTaskWipOpen] = useState(0);

  const [taskOutstandingDet, setTaskOutstandingDet] = useState([]);

  const [faultRepW, setFaultRepW] = useState(0);
  const [faultRepD, setFaultRepD] = useState(0);
  const [faultRepDet, setFaultRepDet] = useState([]);

  const [urgentW, setUrgentW] = useState(0);
  const [urgentD, setUrgentD] = useState(0);
  const [urgentDet, setUrgentDet] = useState([]);

  const [loopDamageW, setLoopDamageW] = useState(0);
  const [loopDamageD, setLoopDamageD] = useState(0);
  const [loopDamageDet, setLoopDamageDet] = useState([]);

  // Modal task active
  const [show, setShow] = useState(false);  
  const [modalType, setModalType] = useState();  

  const [reload, setReload] = React.useState(0);


  const handleClose = () => setShow(false);

  // Handle Modals, Task Details
  const handleShowActive = () => { setModalType('active'); setShow(true); } 
  const handleShowCompleted = () => { setModalType('completed'); setShow(true); } 
  const handleShowWip = () => { setModalType('wip'); setShow(true); } 
  const handleShowOutstanding = () => { setModalType('outstanding'); setShow(true); } 
  const handleShowFaultRep = () => { setModalType('fault_report'); setShow(true); } 
  const handleShowUrgent = () => { setModalType('urgent'); setShow(true); } 
  const handleShowLoopDamage = () => { setModalType('loop_damage'); setShow(true); } 

  const params = { 
    headers: {
    'x-api-key': process.env.REACT_APP_API_KEY
    },
  }

  const renderModal = (modal) => {
    if (modal && modal==="active"){
        taskActiveDet.sort((a, b) => (a.date_created > b.date_created) ? 1 : -1)
        taskActiveDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.tasksActive')} data={taskActiveDet} {...props} />)
      }
    if (modal && modal==="completed"){
        taskCompleteDet.sort((a, b) => (a.date_completed > b.date_completed) ? 1 : -1)
        taskCompleteDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.tasksCompleted')} data={taskCompleteDet} {...props} />)
      }
    if (modal && modal==="wip"){
        taskWipDet.sort((a, b) => (a.date_created > b.date_created) ? 1 : -1)
        taskWipDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.workInProgress')} data={taskWipDet} {...props} />)
      }
    if (modal && modal==="outstanding"){
        taskOutstandingDet.sort((a, b) => (a.date_created > b.date_created) ? 1 : -1)
        taskOutstandingDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.taskOutstanding')} data={taskOutstandingDet} {...props} />)
      }
    if (modal && modal==="fault_report"){
        faultRepDet.sort((a, b) => (a.date_created > b.date_created) ? 1 : -1)
        faultRepDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.modalFaultReport')} data={faultRepDet} {...props} />)
      }
    if (modal && modal==="urgent"){
        urgentDet.sort((a, b) => (a.date_created > b.date_created) ? 1 : -1)
        urgentDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.modalUrgentFault')} data={urgentDet} {...props} />) 
      } 
    if (modal && modal==="loop_damage"){
        loopDamageDet.sort((a, b) => (a.date_created > b.date_created) ? 1 : -1)
        loopDamageDet.reverse();
        return (<SidePanelDet show={show} hide={handleClose} title={t('dashboard.modalLoopDamage')} data={loopDamageDet} {...props} />) 
      } 
  }

  const currentDate = new Date();
  
  //counter for reloading page
  const tick = () => {
    //let newCount = count < 60 ? count + 1 : 0
    setReload((prevState) => prevState < 60 ? prevState +1 : 0);
  }
  //start timer
  useEffect(() => {
    const timer = setInterval(() => tick(), 3000); 
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!isWall) return undefined;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [isWall]);

  useEffect(() => {
    if (!isWall) {
      setUnattended(false);
      return undefined;
    }
    const armIdle = () => {
      setUnattended(false);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => setUnattended(true), 12000);
    };
    armIdle();
    window.addEventListener('mousemove', armIdle);
    window.addEventListener('keydown', armIdle);
    window.addEventListener('touchstart', armIdle);
    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      window.removeEventListener('mousemove', armIdle);
      window.removeEventListener('keydown', armIdle);
      window.removeEventListener('touchstart', armIdle);
    };
  }, [isWall]);

  // reset counters and refresh
  useEffect(() =>{
    if (reload===0) {
        setServiceCall({});
        setReactiveData({});
        setPpmData({});
        setSlaAttendanceData({});
        setSlaRepairData({});
        setUsers([]);
        if (!mapLoaded) {
          setMapTasks([]);
        }
        setTaskActiveW(0);
        setTaskActiveD(0);
        setTaskActiveDet([]);
        setTaskCompleteW(0);
        setTaskCompleteD(0);
        setTaskCompleteDet([]);
        setTaskWipW(0);
        setTaskWipD(0);
        setTaskWipDet([]);
        setTaskWipOpen(0);
        setTaskOutstandingDet([])
        setFaultRepW(0);
        setFaultRepD(0);
        setFaultRepDet([]);
        setUrgentW(0);
        setUrgentD(0);
        setUrgentDet([]);
        setUrgentUnassigned(0);
        setUrgentAged(0);
        setOutstandingSlaOverdue(0);
        setSlaOverdue(0);
        setLoopDamageW(0);
        setLoopDamageD(0);
        setLoopDamageDet([]);

        setTasks([]);
        setLoading(false);
    }
  },[reload])


  // load users
  useEffect(()=>{
    if (reload===0) {
      API.get('users','/', params)
      .then(res => {
          const userList = res.Users.map((item) => {
            const user = { id: item.Username, name: "-" };
            item.Attributes.forEach((e) => {
              if (e.Name === 'name') user.name = e.Value;
            });
            return user;
          });
          setUsers(userList);
      })
      .catch(() => setDataError(true));
    }
  },[reload])

  // Load tasks for side board, graphs and map
	useEffect(()=>{
		const fetchTasks = async () => {
      try {
      let link = [];
      const taskListFiltered = [];
      const today = new Date();
      const taskList = await API.get('task_by_month','/' + currentDate.toISOString(), params)
      const MAX_MAP_MARKERS = 500;
      
      let countActiveD = 0, countActiveW = 0, detActive = [],
      countCompleteD = 0, countCompleteW = 0, detComplete = [],
      countWipD = 0, countWipW = 0, detWip = [],
      detOutstanding = [],
      countUrgentD = 0, countUrgentW = 0, detUrgent = [],
      countUrgentUnassigned = 0, countUrgentAged = 0, countSlaOverdue = 0,
      countWipOpen = 0,
      countFaultRepD = 0, countFaultRepW = 0, detFaultRep = [],
      countLoopDamageD = 0, countLoopDamageW = 0, detLoopDamage = [];
      
      for (let i = 0; i < taskList.length; i++) {
        
        let days_create 
        let days_comp 

        // Count days from task completed
        if (taskList[i].date_completed){
          const d = new Date(taskList[i].date_completed);               
          taskList[i].date_upd = ("0"+d.getDate()).slice(-2) + "-" + ("0"+(d.getMonth()+1)).slice(-2) + "-" + d.getFullYear() + " "+ d.getHours() +":"+ ("0"+d.getMinutes()).slice(-2);
          days_comp = Math.round((today.getTime() - d.getTime())/(1000*60*60*24))         
        } else {
          // Count days from task started
          if (taskList[i].date_started){
            const d = new Date(taskList[i].date_started);               
            taskList[i].date_upd = ("0"+d.getDate()).slice(-2) + "-" + ("0"+(d.getMonth()+1)).slice(-2) + "-" + d.getFullYear() + " "+ d.getHours() +":"+ ("0"+d.getMinutes()).slice(-2);                   
            days_create = Math.round((today.getTime()-d.getTime())/(1000*60*60*24)) 
          } else {
            // Count days from task created
            if (taskList[i].date_created){
              const d = new Date(taskList[i].date_created);               
              taskList[i].date_upd = ("0"+d.getDate()).slice(-2) + "-" + ("0"+(d.getMonth()+1)).slice(-2) + "-" + d.getFullYear() + " "+ d.getHours() +":"+ ("0"+d.getMinutes()).slice(-2);               
              days_create = Math.round((today.getTime()-d.getTime())/(1000*60*60*24)) 
            }
          }
        }

        if (taskList[i].date_created && taskList[i].date_started) {
          const date_create = new Date(taskList[i].date_created); 
          const date_start = new Date(taskList[i].date_started); 
          const diff = new Date(date_start - date_create);
          taskList[i].sla_attend_compliance = diff.getUTCHours();
        }

        if (taskList[i].date_completed && taskList[i].date_started) {
          const date_complete = new Date(taskList[i].date_completed); 
          const date_start = new Date(taskList[i].date_started); 
          const diff = new Date(date_complete - date_start);
          taskList[i].sla_repair_compliance = diff.getUTCHours();

        }

        // Count tasks Active, Completed, WIP, Fault and Urgent 
        switch (taskList[i].task_status) {
					case 'Created':
            taskList[i].cssTaskStatus = 'created';
            taskList[i].css = "task_created";
            if (days_create === 0) countActiveD++;                 
            if (days_create <= 8)  { countActiveW++; detActive.push(taskList[i]) }
            if (days_create > 8) detOutstanding.push(taskList[i])
						break;
					case 'Accepted':
            taskList[i].cssTaskStatus = 'accepted';
            taskList[i].css = "task_accepted";
            if (days_create === 0) countActiveD++; 
            if (days_create <= 8)  { countActiveW++; detActive.push(taskList[i]) }
            if (days_create > 8) detOutstanding.push(taskList[i])
            break;
          case 'Started':
            taskList[i].cssTaskStatus = 'started';
            taskList[i].css = "task_started";
            if (days_create === 0) countActiveD++; 
            if (days_create <= 8) { countActiveW++; detActive.push(taskList[i]) }
            if (days_create > 8) detOutstanding.push(taskList[i])
						break;
					case 'Completed':
            taskList[i].cssTaskStatus = 'completed';
            taskList[i].css = "task_completed";
            if (days_comp === 0) countCompleteD++; 
            if (days_comp <= 8)  { countCompleteW++; detComplete.push(taskList[i]) }
						break;
					case 'WIP':
            taskList[i].cssTaskStatus = 'wip';
            taskList[i].css = "task_wip";
            countWipOpen++;
            if (days_create === 0) countWipD++; 
            if (days_create <= 8)  { countWipW++; detWip.push(taskList[i]) }
            if (days_create > 8) detOutstanding.push(taskList[i])
            break;
					default:
            taskList[i].cssTaskStatus = 'created';
            taskList[i].css = "task_created";
            if (days_create === 0) countActiveD++; 
            if (days_create <= 8)  { countActiveW++; detActive.push(taskList[i]) }
            if (days_create > 8) detOutstanding.push(taskList[i])
        }

        if (!taskList[i].date_completed && isSlaOverdue(taskList[i], today.getTime())) {
          countSlaOverdue++;
        }

        if (taskList[i].urgent_fault==='Urgent' && taskList[i].category === 'Reactive') {  
          if (days_create === 0 || days_comp === 0) countUrgentD++;      
          if (days_create <= 8 || days_comp <= 8) { countUrgentW++; detUrgent.push(taskList[i]) }
          if (!taskList[i].date_completed) {
            if (!taskList[i].contractor) countUrgentUnassigned++;
            if (days_create > 7) countUrgentAged++;
          }
        }	

        if (taskList[i].ri_q6_detection_problem) {
          if (days_create === 0 || days_comp === 0) countLoopDamageD++;
          if (days_create <= 8 || days_comp <= 8) countLoopDamageW++;
          // Add all tasks with loop damage problems, regardless of date
          detLoopDamage.push(taskList[i]);
        }
        
        if (taskList[i].contractor) {
					taskList[i].contractor_str = taskList[i].contractor + " - "
				} else {
					taskList[i].contractor_str = ""
        }

        if (!taskList[i].urgent_fault){
          taskList[i].urgent_fault = '-'
        }

        if (taskList[i].category === 'Reactive') {
          if (days_create === 0 || days_comp === 0) countFaultRepD++; 
          if (days_create <= 8 || days_comp <= 8) { countFaultRepW++; detFaultRep.push(taskList[i]) }
        }

        let lat = 0;
        let long = 0;
        if (taskList[i].latitude) {
          lat = parseFloat(taskList[i].latitude)
        }
        if (taskList[i].longitude) {
          long = parseFloat(taskList[i].longitude)
        }    
 
        if (taskList[i].user_created) {
          const usr = users.find(usr => usr.id === taskList[i].user_created)
          if (usr) 
            taskList[i].user_created_str = usr.name 
          else 
           taskList[i].user_created_str = "-" 
        } 

        // graph shows the last 30 days
        if (days_comp <= 30 || days_create <= 30) {          
          taskListFiltered.push(taskList[i]);
        }

        // map shows actives and completed recently 
        // || days_create <= 8
        
        if (!mapLoaded && link.length < MAX_MAP_MARKERS) {
          if (days_comp <= 1 || days_comp === undefined) {
            const hasCoords = Number.isFinite(lat) && Number.isFinite(long) && !(lat === 0 && long === 0);
            if (hasCoords) {
            const pinStatus = pinStatusKey(taskList[i].cssTaskStatus);
            const siteTitle = [taskList[i].site_ref, taskList[i].task_ref].filter(Boolean).join(' · ');
            link.push({
              coords: { lat: lat, lng: long },
              status: pinStatus,
              title: siteTitle,
              siteRef: taskList[i].site_ref || '',
              content: markerInfoHtml(taskList[i], pinStatus),
            });
            }
          }
        }

      }   

      setTaskActiveD(countActiveD);
      setTaskActiveW(countActiveW);
      setTaskActiveDet(detActive);
      setTaskCompleteD(countCompleteD);
      setTaskCompleteW(countCompleteW);
      setTaskCompleteDet(detComplete);
      setTaskWipD(countWipD);
      setTaskWipW(countWipW);
      setTaskWipDet(detWip);
      setTaskWipOpen(countWipOpen);
      setTaskOutstandingDet(detOutstanding);
      setOutstandingSlaOverdue(detOutstanding.filter((item) => isSlaOverdue(item, today.getTime())).length);
      setSlaOverdue(countSlaOverdue);
      setUrgentD(countUrgentD);
      setUrgentW(countUrgentW);
      setUrgentDet(detUrgent);
      setUrgentUnassigned(countUrgentUnassigned);
      setUrgentAged(countUrgentAged);
      setFaultRepD(countFaultRepD);
      setFaultRepW(countFaultRepW);
      setFaultRepDet(detFaultRep);
      setLoopDamageD(countLoopDamageD);
      setLoopDamageW(countLoopDamageW);
      setLoopDamageDet(detLoopDamage);

      setTasks(taskListFiltered);

      if (!mapLoaded) {
        setMapTasks(link);
        setMapLoaded(true);
      }
      setDataUpdatedAt(new Date());
      setDataError(false);
      setLoading(true);
      } catch (err) {
        setDataError(true);
      }
    }
    if (users.length > 0)
      fetchTasks();	

  }, [users]);

  const mapView = getMapView();
  const visibleMapTasks = mapTasks.filter((item) => !hiddenPins[item.status]);
  const pinCounts = mapTasks.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});
  const mappedSites = new Set(mapTasks.map((item) => item.siteRef).filter(Boolean)).size;
  const dataAgeMs = dataUpdatedAt ? (now.getTime() - dataUpdatedAt.getTime()) : null;
  const dataStatus = dataError
    ? { key: 'lost', label: t('dashboard.connectionLost') }
    : !dataUpdatedAt
      ? { key: 'waiting', label: t('dashboard.waiting') }
      : dataAgeMs > 4 * 60 * 1000
        ? { key: 'stale', label: t('dashboard.stale') }
        : { key: 'live', label: t('dashboard.live') };
  const urgentOpenCount = urgentDet.filter((item) => !item.date_completed).length;
  const wallAlert = urgentAged > 0
    ? { tone: 'danger', text: t(urgentAged === 1 ? 'dashboard.alertUrgentAgedOne' : 'dashboard.alertUrgentAgedMany', { count: urgentAged }) }
    : slaOverdue > 0
      ? { tone: 'danger', text: t(slaOverdue === 1 ? 'dashboard.alertSlaOne' : 'dashboard.alertSlaMany', { count: slaOverdue }) }
      : urgentUnassigned > 0
        ? { tone: 'warning', text: t(urgentUnassigned === 1 ? 'dashboard.alertUnassignedOne' : 'dashboard.alertUnassignedMany', { count: urgentUnassigned }) }
        : null;
  const urgentRowTone = urgentAged > 0
    ? 'danger'
    : (urgentOpenCount > 0 || urgentUnassigned > 0)
      ? 'warning'
      : 'neutral';
  const setMapMode = (mode) => {
    setMapFrame(mode);
    setMapFrameSeq((seq) => seq + 1);
  };

  const mapOptions = useMemo(() => ({
    center: mapView.center,
    zoom: mapView.zoom,
    scrollwheel: true,
    mapId: "4ada5c0171a1e340",
    colorScheme: mapColorScheme(theme, viewMode),
  }), [mapView.center.lat, mapView.center.lng, mapView.zoom, theme, viewMode]);

  const mapProps = {
    options: mapOptions,
    onMount: addMarkers,
    onMountProps: visibleMapTasks,
    frameProps: mapTasks,
    viewCommand: { mode: mapFrame, seq: mapFrameSeq },
    className: 'gmaps'
  };
  const MemoMap = useCallback(<Map {...mapProps} />, [mapProps]); 

  // Fault Category Graph
  const getFault_Category = () => {
    
    const fault_category = {
      "Reactive": tasks.filter(item => item.category === 'Reactive' && item.date_completed).length,
      "PPM": tasks.filter(item => item.category === 'PPM' && item.date_completed).length,
      "Switch off/on": tasks.filter(item => item.category === 'Switch off / on' && item.date_completed).length,
      "Upgrade": tasks.filter(item => item.category === 'Upgrade' && item.date_completed).length,
      "Signs": tasks.filter(item => item.category === 'Signs' && item.date_completed).length,
      "Other": tasks.filter(item => item.category === 'Other' && item.date_completed).length
    }
    const fault_category_active = {
      "Reactive": tasks.filter(item => item.category === 'Reactive' && !item.date_completed).length,
      "PPM": tasks.filter(item => item.category === 'PPM' && !item.date_completed).length,
      "Switch off/on": tasks.filter(item => item.category === 'Switch off / on' && !item.date_completed).length,
      "Upgrade": tasks.filter(item => item.category === 'Upgrade' && !item.date_completed).length,
      "Signs": tasks.filter(item => item.category === 'Signs' && !item.date_completed).length,
      "Other": tasks.filter(item => item.category === 'Other' && !item.date_completed).length
    }

    const filteredFault = Object.keys(fault_category);
    const filteredFaultActive = Object.keys(fault_category_active); 
    const data = {
      labels: filteredFault.map(tCatalog),
      datasets: [
        {
        label: 'Completed',
        ...completedSeries,
        data: filteredFault.map(key => fault_category[key]),
        stack: '1'      
        },
        {
          label: 'Active',
          ...activeSeries,
          data: filteredFaultActive.map(key => fault_category_active[key]),
          stack: '1'      
          },
      ],
    };
    setServiceCall(data);
  };

  // Reactive tasks
  const getReactive_Category = () => {

    const reactive = {
      "Controller Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Controller Fault' && item.date_completed).length, 
      "Detector Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Detector Fault' && item.date_completed).length, 
      "PBU Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'PBU Fault' && item.date_completed).length, 
      "Electrical Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Electrical Fault' && item.date_completed).length, 
      "Lamp Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Lamp Fault' && item.date_completed).length, 
      "Comms Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Comms Fault' && item.date_completed).length,
      "Sign Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Sign Fault' && item.date_completed).length, 
      "Knockdown": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Knockdown' && item.date_completed).length, 
      "Other": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Other' && item.date_completed).length
    }

    const reactive_active = {
      "Controller Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Controller Fault' && !item.date_completed).length, 
      "Detector Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Detector Fault' && !item.date_completed).length, 
      "PBU Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'PBU Fault' && !item.date_completed).length, 
      "Electrical Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Electrical Fault' && !item.date_completed).length, 
      "Lamp Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Lamp Fault' && !item.date_completed).length, 
      "Comms Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Comms Fault' && !item.date_completed).length,
      "Sign Fault": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Sign Fault' && !item.date_completed).length, 
      "Knockdown": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Knockdown' && !item.date_completed).length, 
      "Other": tasks.filter(item => item.category === 'Reactive' && item.type_fault === 'Other' && !item.date_completed).length
    }

    const filteredReactive = Object.keys(reactive);
    const filteredReactiveActive = Object.keys(reactive_active); 

    const data = {
      labels: filteredReactive.map(tCatalog),
      datasets: [
        {
        label: 'Completed',
        ...completedSeries,
        data: filteredReactive.map(key => reactive[key]),
        stack: '1'      
        },
        {
          label: 'Active',
          ...activeSeries,
          data: filteredReactiveActive.map(key => reactive_active[key]),
          stack: '1'      
          },
      ],
    };
    setReactiveData(data);
  };

  // PPM Tasks
  const getPPM_Category = () => {

    const ppm = {
      'Periodic Insp': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Periodic Inspection' && item.date_completed).length, 
      'Annual Insp (Sig)': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Annual Inspection (Signals)' && item.date_completed).length, 
      'Annual Insp (Struc)': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Annual Inspection (Structures)' && item.date_completed).length,
      'Annual Insp (Signs)': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Annual Inspection (Signs)' && item.date_completed).length,
      'Routine Maint': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Routine Maintenance' && item.date_completed).length,
      'Non LED Lamp rep': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Non LED Lamp replacement' && item.date_completed).length
    }
 
    const ppm_active = {
      'Periodic Insp': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Periodic Inspection' && !item.date_completed).length, 
      'Annual Insp (Sig)': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Annual Inspection (Signals)' && !item.date_completed).length, 
      'Annual Insp (Struc)': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Annual Inspection (Structures)' && !item.date_completed).length,
      'Annual Insp (Signs)': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Annual Inspection (Signs)' && !item.date_completed).length,
      'Routine Maint': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Routine Maintenance' && !item.date_completed).length,
      'Non LED Lamp rep': tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Non LED Lamp replacement' && !item.date_completed).length
    }

    const filteredPPM = Object.keys(ppm); 
    const filteredPPMActive = Object.keys(ppm_active);
    
    const data = {
      labels: filteredPPM.map(tCatalog),
      datasets: [
        {
        label: 'Completed',
        ...completedSeries,
        data: filteredPPM.map(key => ppm[key]),
        stack: '1'      
        },
        {
          label: 'Active',
          ...activeSeries,
          data: filteredPPMActive.map(key => ppm_active[key]),
          stack: '1'      
          },
      ],
    };
    setPpmData(data);
  };

  // SLA Attendance Compliance
  const getSLA_Attendance = () => {

    const sla_compliance = {
      "Reactive": tasks.filter(item => item.category === 'Reactive' && item.sla_attend_compliance <= item.sla_attend).length,
      "PPM Inspections": tasks.filter(item => item.category === 'PPM' && 
                            (item.type_fault === 'Periodic Inspection' ||
                            item.type_fault === 'Annual Inspection (Signals)' || 
                            item.type_fault === 'Annual Inspection (Structures)' || 
                            item.type_fault === 'Annual Inspection (Signs)')
                            && (item.sla_attend_compliance <= item.sla_attend && item.sla_attend >= 0)).length,
      "PPM Routine Maint": tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Routine Maintenance' && item.sla_attend_compliance <= item.sla_attend).length,
    }
    const sla_non_compliance = {
      "Reactive": tasks.filter(item => item.category === 'Reactive' && item.sla_attend_compliance > item.sla_attend).length,
      "PPM Inspections": tasks.filter(item => item.category === 'PPM' &&
                            (item.type_fault === 'Periodic Inspection' ||
                            item.type_fault === 'Annual Inspection (Signals)' || 
                            item.type_fault === 'Annual Inspection (Structures)' || 
                            item.type_fault === 'Annual Inspection (Signs)')
                             && (item.sla_attend_compliance > item.sla_attend && item.sla_attend > 0)).length,
      "PPM Routine Maint": tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Routine Maintenance' && item.sla_attend_compliance > item.sla_attend).length,
      }

    const filteredComp = Object.keys(sla_compliance); 
    const filteredNonComp = Object.keys(sla_non_compliance); 
    const data = {
      labels: filteredComp.map(chartCatLabel),
      datasets: [
        {
        label: t('dashboard.compliance'),
        backgroundColor: chartColors.success,
        hoverBackgroundColor: chartColors.successHover,
        data: filteredComp.map(key => sla_compliance[key]),
        stack: '1'      
        },
        {
          label: t('dashboard.nonCompliance'),
          backgroundColor: chartColors.warning,
          hoverBackgroundColor: chartColors.warningHover,
          data: filteredNonComp.map(key => sla_non_compliance[key]),
          stack: '1'      
          },
      ],
    };
    setSlaAttendanceData(data);
  };

  // SLA Repair Compliance
  const getSLA_Repair = () => {

    const sla_compliance = {
      "Reactive": tasks.filter(item => item.category === 'Reactive' && item.sla_repair_compliance <= parseFloat(item.sla_repair)).length,
      "PPM Inspections": tasks.filter(item => item.category === 'PPM' &&
                            (item.type_fault === 'Periodic Inspection' ||
                            item.type_fault === 'Annual Inspection (Signals)' || 
                            item.type_fault === 'Annual Inspection (Structures)' || 
                            item.type_fault === 'Annual Inspection (Signs)')
                            && (item.sla_repair_compliance <= parseFloat(item.sla_repair) && item.sla_repair >= 0)).length,
      "PPM Routine Maint": tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Routine Maintenance' && item.sla_repair_compliance <= parseFloat(item.sla_repair)).length,
     }
    const sla_non_compliance = {
      "Reactive": tasks.filter(item => item.category === 'Reactive' && item.sla_repair_compliance > parseFloat(item.sla_repair)).length,
      "PPM Inspections": tasks.filter(item => item.category === 'PPM' &&
                            (item.type_fault === 'Periodic Inspection' ||
                            item.type_fault === 'Annual Inspection (Signals)' || 
                            item.type_fault === 'Annual Inspection (Structures)' || 
                            item.type_fault === 'Annual Inspection (Signs)')
                            && (item.sla_repair_compliance > parseFloat(item.sla_repair) && item.sla_repair > 0)).length,
      "PPM Routine Maint": tasks.filter(item => item.category === 'PPM' && item.type_fault === 'Routine Maintenance' && item.sla_repair_compliance > parseFloat(item.sla_repair)).length,
     }

    const filteredComp = Object.keys(sla_compliance); 
    const filteredNonComp = Object.keys(sla_non_compliance); 
    const data = {
      labels: filteredComp.map(chartCatLabel),
      datasets: [
        {
        label: t('dashboard.compliance'),
        backgroundColor: chartColors.success,
        hoverBackgroundColor: chartColors.successHover,
        data: filteredComp.map(key => sla_compliance[key]),
        stack: '1'      
        },
        {
          label: t('dashboard.nonCompliance'),
          backgroundColor: chartColors.warning,
          hoverBackgroundColor: chartColors.warningHover,
          data: filteredNonComp.map(key => sla_non_compliance[key]),
          stack: '1'      
          },
      ],
    };
    setSlaRepairData(data);
  };

  useEffect(() => {
      if (!loading) {
        getFault_Category();
        getReactive_Category();
        getPPM_Category();
        getSLA_Attendance();
        getSLA_Repair();  
    }
  },[tasks, theme, language]);

  const wrapTick = (value) => {
    if (typeof value !== 'string') return value;
    const lines = {
      'Controller Fault': ['Controller', 'Fault'],
      'Detector Fault': ['Detector', 'Fault'],
      'PBU Fault': ['PBU', 'Fault'],
      'Electrical Fault': ['Electrical', 'Fault'],
      'Lamp Fault': ['Lamp', 'Fault'],
      'Comms Fault': ['Comms', 'Fault'],
      'Sign Fault': ['Sign', 'Fault'],
      'Switch off/on': ['Switch', 'off/on'],
      'PPM Inspections': ['PPM', 'Inspections'],
      'PPM Routine Maint': ['PPM', 'Routine'],
      'Periodic Insp': ['Periodic', 'Insp'],
      'Annual Insp (Sig)': ['Annual', 'Signals'],
      'Annual Insp (Struc)': ['Annual', 'Struct.'],
      'Annual Insp (Signs)': ['Annual', 'Signs'],
      'Routine Maint': ['Routine', 'Maint'],
      'Non LED Lamp rep': ['Non-LED', 'Lamp'],
    };
    if (lines[value]) return lines[value];
    if (value.length <= 12) return value;
    const words = value.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      const mid = Math.ceil(words.length / 2);
      return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
    }
    return value;
  };

  const options = {
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        boxWidth: 10,
        fontSize: 13,
        fontColor: chartColors.textSecondary,
        padding: 16,
        usePointStyle: true,
      },
    },
    scales: {
      xAxes: [{
        stacked: true,
        gridLines: { display: false, drawBorder: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          fontSize: 12,
          fontStyle: 'bold',
          fontColor: chartColors.textSecondary,
          lineHeight: 1.2,
          padding: 10,
          callback: function(value) {
            const label = typeof value === 'string' ? value : (this.chart && this.chart.data.labels[value]);
            return wrapTick(label || value);
          },
        },
      }],
      yAxes: [{
        stacked: true,
        gridLines: {
          color: chartColors.grid,
          zeroLineColor: chartColors.gridZero,
          drawBorder: false,
        },
        ticks: {
          beginAtZero: true,
          fontSize: 13,
          fontColor: chartColors.textSecondary,
          padding: 8,
          precision: 0,
        },
      }],
    },
    layout: { padding: { top: 8, right: 10, left: 6, bottom: 22 } },
    tooltips: {
      backgroundColor: chartColors.tooltipBg || chartColors.textPrimary,
      titleFontColor: chartColors.tooltipColor || '#fff',
      bodyFontColor: chartColors.tooltipColor || '#fff',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 8,
      xPadding: 10,
      yPadding: 8,
    },
  };

  const cellText = (value) => (value ? String(value) : '—');

  const statusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('completed')) return 'dash-status dash-status-done';
    if (s.includes('wip')) return 'dash-status dash-status-wip';
    if (s.includes('started')) return 'dash-status dash-status-started';
    if (s.includes('accepted')) return 'dash-status dash-status-accepted';
    if (s.includes('created')) return 'dash-status dash-status-created';
    return 'dash-status';
  };

  const daysOpen = (iso) => {
    if (!iso) return 0;
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 0;
    return Math.round(ms / (1000 * 60 * 60 * 24));
  };
  const outstandingOldestDays = taskOutstandingDet.reduce(
    (max, item) => Math.max(max, daysOpen(item.date_created)),
    0
  );

  const recentFaults = [...tasks]
    .filter((item) => item.category === 'Reactive')
    .sort((a, b) => new Date(b.date_created || 0) - new Date(a.date_created || 0))
    .slice(0, 6);

  const upcomingInspections = [...tasks]
    .filter((item) => item.category === 'PPM' && !item.date_completed)
    .sort((a, b) => new Date(a.date_created || 0) - new Date(b.date_created || 0))
    .slice(0, 6);

  const renderTaskRows = (rows) => (
    rows.length === 0 ? (
      <tr>
        <td colSpan="4" className="dash-table-empty">{t('dashboard.noRecords')}</td>
      </tr>
    ) : rows.map((item) => {
      const { day, time } = splitStamp(item.date_upd);
      return (
      <tr key={item.taskId} onClick={() => props.history.push('/task_det/' + item.taskId)}>
        <td className="dash-td-date">
          <span className="dash-td-day">{day || '—'}</span>
          {time ? <span className="dash-td-time">{time}</span> : null}
        </td>
        <td className="dash-td-site" title={`${cellText(item.site_ref)} — ${cellText(item.site_address)}`}>
          <span className="dash-td-asset">{cellText(item.site_ref)}</span>
          <span className="dash-td-location">{cellText(item.site_address)}</span>
        </td>
        <td className="dash-td-type" title={cellText(tCatalog(item.type_fault || item.category))}>
          <span>{cellText(tCatalog(item.type_fault || item.category))}</span>
        </td>
        <td className="dash-td-status"><span className={statusClass(item.task_status)} title={tStatus(item.task_status)}>{tStatus(item.task_status)}</span></td>
      </tr>
      );
    })
  );

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const root = document.documentElement;
      if (root.requestFullscreen) root.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const exitWall = () => {
    setMapExpanded(false);
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen();
    setViewMode('standard');
  };

  return (     
     
    <div className={`Dashboard${isWall ? ' is-wall' : ''}${mapExpanded ? ' is-map-expanded' : ''}${isWall && unattended ? ' is-unattended' : ''}`}>
      {isWall ? (
        <header className="dash-wall-bar">
          <div className="dash-wall-brand">
            {headerLogo ? <img src={headerLogo} alt="" /> : null}
            <strong>{t('dashboard.operationsOverview')}</strong>
          </div>
          <div className="dash-wall-status">
            <span className={`dash-wall-health is-${dataStatus.key}`}>
              <span className="dash-live-dot" aria-hidden="true" />
              {dataStatus.label}
            </span>
            <span className="dash-wall-stat">{t('dashboard.updated')} <strong>{dataUpdatedAt ? formatClock(dataUpdatedAt) : '—'}</strong></span>
            <span className="dash-wall-stat">{t('dashboard.time')} <strong>{formatClock(now)}</strong></span>
            <span className="dash-wall-stat">{t('dashboard.monitoredSites')} <strong>{mappedSites}</strong></span>
            <span className="dash-wall-stat">{t('dashboard.mapTasks')} <strong>{mapTasks.length}</strong></span>
          </div>
          <div className="dash-wall-actions">
            <button type="button" className="dash-wall-btn" onClick={toggleFullscreen} title={t('common.fullscreen')}>
              <FaExpandArrowsAlt />
              <span>{t('common.fullscreen')}</span>
            </button>
            <button type="button" className="dash-wall-btn" onClick={exitWall} title={t('dashboard.exitWall')}>
              <FaTimes />
              <span>{t('common.exit')}</span>
            </button>
          </div>
        </header>
      ) : (
        <PageHeader
          className="dash-page-header"
          title={t('pages.dashboard.title')}
          subtitle={t('pages.dashboard.subtitle')}
          actions={
            <>
              <Button size="sm" variant="light" className="dash-chip" onClick={() => props.history.push('/tasks')}>
                <FaTasks /> {t('nav.tasks')}
              </Button>
              <Button size="sm" variant="light" className="dash-chip" onClick={() => props.history.push('/assets')}>
                <FaBuilding /> {t('nav.assets')}
              </Button>
              <Button size="sm" variant="light" className="dash-chip" onClick={() => props.history.push('/asset_locator')}>
                <FaMap /> {t('common.map')}
              </Button>
            </>
          }
        />
      )}
      {isWall && wallAlert ? (
        <div className={`dash-wall-alert is-${wallAlert.tone}`} role="status">{wallAlert.text}</div>
      ) : null}

      <section className="dash-section dash-kpi-row">
        <div className="dash-kpi-grid dash-kpi-grid-main">
            <SidePanel 
              size="lg"
              bg="primary"
              img="cog"
              task_day={taskActiveD}
              task_month={taskActiveW}
              title={t('dashboard.tasksActive')}
              periodLabel={isWall ? t('dashboard.activeLast7') : t('dashboard.last7days')}
              todayLabel={t('dashboard.createdToday')}
              onClick={handleShowActive}
            />  
            <SidePanel 
              size="lg"
              bg="success"
              img="tasks"
              task_day={taskCompleteD}
              task_month={taskCompleteW}
              title={t('dashboard.tasksCompleted')}
              periodLabel={isWall ? t('dashboard.completedLast7') : t('dashboard.last7days')}
              todayLabel={t('dashboard.completedToday')}
              onClick={handleShowCompleted}
            /> 
            <SidePanel 
              size="lg"
              bg="info"
              img="wrench"
              task_day={taskWipD}
              task_month={isWall ? taskWipOpen : taskWipW}
              title={t('dashboard.workInProgress')}
              periodLabel={isWall ? t('dashboard.currentlyInProgress') : t('dashboard.last7days')}
              todayLabel={t('dashboard.inProgressToday')}
              onClick={handleShowWip}
            /> 
            <SidePanel 
              size="lg"
              bg={outstandingSlaOverdue > 0 ? 'danger' : 'dark'}
              img="clock"
              task_only={taskOutstandingDet.length}
              title={t('dashboard.taskOutstanding')}
              periodLabel={t('dashboard.openOver7days')}
              extraNote={
                outstandingSlaOverdue > 0
                  ? `${t('dashboard.overdueCount', { count: outstandingSlaOverdue })}${outstandingOldestDays > 0 ? ` · ${t('dashboard.oldestDays', { days: outstandingOldestDays })}` : ''}`
                  : outstandingOldestDays > 0 ? t('dashboard.oldestDays', { days: outstandingOldestDays }) : null
              }
              onClick={handleShowOutstanding}
            /> 
        </div>
      </section>

      <section className="dash-section dash-ops-row">
        <Card className="dash-panel dash-map-card">
          <Card.Header className="dash-card-head dash-map-header">
            <div className="dash-card-title">
              <FaMapMarkerAlt />
              <h3>{isWall ? t('dashboard.liveTaskMap') : t('dashboard.taskMap')}</h3>
            </div>
            <div className="dash-map-tools">
              {isWall ? (
                <div className="dash-map-frame-tools">
                  <button
                    type="button"
                    className={`dash-map-frame-btn${mapFrame === 'sites' ? ' is-active' : ''}`}
                    onClick={() => setMapMode('sites')}
                    title={t('dashboard.fitSitesTitle')}
                  >
                    <FaSearchLocation /> {t('dashboard.fitSites')}
                  </button>
                  <button
                    type="button"
                    className={`dash-map-frame-btn${mapFrame === 'overview' ? ' is-active' : ''}`}
                    onClick={() => setMapMode('overview')}
                    title={t('dashboard.overviewTitle')}
                  >
                    <FaGlobe /> {t('dashboard.overview')}
                  </button>
                </div>
              ) : null}
              <div className="map-legend" role="group" aria-label={t('dashboard.filterMarkers')}>
                <LegendPin status="created" label={t('status.created')} count={pinCounts.created || 0} hidden={!!hiddenPins.created} onToggle={() => setHiddenPins((prev) => ({ ...prev, created: !prev.created }))} showTitle={t('dashboard.showPin', { label: t('status.created') })} hideTitle={t('dashboard.hidePin', { label: t('status.created') })} />
                <LegendPin status="accepted" label={t('status.accepted')} count={pinCounts.accepted || 0} hidden={!!hiddenPins.accepted} onToggle={() => setHiddenPins((prev) => ({ ...prev, accepted: !prev.accepted }))} showTitle={t('dashboard.showPin', { label: t('status.accepted') })} hideTitle={t('dashboard.hidePin', { label: t('status.accepted') })} />
                <LegendPin status="started" label={t('status.started')} count={pinCounts.started || 0} hidden={!!hiddenPins.started} onToggle={() => setHiddenPins((prev) => ({ ...prev, started: !prev.started }))} showTitle={t('dashboard.showPin', { label: t('status.started') })} hideTitle={t('dashboard.hidePin', { label: t('status.started') })} />
                <LegendPin status="wip" label={t('status.wip')} count={pinCounts.wip || 0} hidden={!!hiddenPins.wip} onToggle={() => setHiddenPins((prev) => ({ ...prev, wip: !prev.wip }))} showTitle={t('dashboard.showPin', { label: t('status.wip') })} hideTitle={t('dashboard.hidePin', { label: t('status.wip') })} />
                <LegendPin status="completed" label={t('status.completed')} count={pinCounts.completed || 0} hidden={!!hiddenPins.completed} onToggle={() => setHiddenPins((prev) => ({ ...prev, completed: !prev.completed }))} showTitle={t('dashboard.showPin', { label: t('status.completed') })} hideTitle={t('dashboard.hidePin', { label: t('status.completed') })} />
              </div>
              <button
                type="button"
                className="dash-map-expand"
                onClick={() => setMapExpanded((open) => !open)}
                title={mapExpanded ? t('dashboard.closeMap') : t('dashboard.enlargeMap')}
              >
                {mapExpanded ? <FaCompress /> : <FaExpand />}
                {mapExpanded ? t('common.close') : t('common.enlarge')}
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="map-wrap task-map-canvas">
              {MemoMap}
            </div>
          </Card.Body>
        </Card>
        <div className="dash-panel dash-incidents-card">
          <div className="dash-card-head">
            <div className="dash-card-title">
              <FaExclamationTriangle />
              <h3>{t('dashboard.incidents')}</h3>
            </div>
          </div>
          <div className="dash-incidents-list">
            <button type="button" className={`dash-incident-row is-${urgentRowTone}`} onClick={handleShowUrgent}>
              <span className="dash-incident-copy">
                <span className="dash-incident-title">{t('dashboard.urgentFaults')}</span>
                <span className="dash-incident-meta">
                  {[
                    t('dashboard.urgentReactive'),
                    t('dashboard.last7days'),
                    t('dashboard.countToday', { count: urgentD }),
                    urgentOpenCount > 0 ? t('dashboard.countOpen', { count: urgentOpenCount }) : null,
                    urgentUnassigned > 0 ? t('dashboard.countUnassigned', { count: urgentUnassigned }) : null,
                    urgentAged > 0 ? t('dashboard.countOpenOver7', { count: urgentAged }) : null,
                  ].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="dash-incident-value">{urgentW}</span>
            </button>
            <button type="button" className="dash-incident-row is-neutral" onClick={handleShowFaultRep}>
              <span className="dash-incident-copy">
                <span className="dash-incident-title">{t('dashboard.faultReports')}</span>
                <span className="dash-incident-meta">{[t('dashboard.allReactive'), t('dashboard.last7days'), t('dashboard.countToday', { count: faultRepD })].join(' · ')}</span>
              </span>
              <span className="dash-incident-value">{faultRepW}</span>
            </button>
            <button type="button" className="dash-incident-row is-neutral" onClick={handleShowLoopDamage}>
              <span className="dash-incident-copy">
                <span className="dash-incident-title">{t('dashboard.detectionDamage')}</span>
                <span className="dash-incident-meta">{t('dashboard.detectorIssues')}</span>
              </span>
              <span className="dash-incident-value">{loopDamageDet.length}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="dash-section">
        <div className="row dash-tables">
          <div className="col-lg-6">
            <div className="dash-panel dash-table-card">
              <div className="dash-card-head dash-table-header">
                <div className="dash-card-title">
                  <FaWrench />
                  <h3>{t('dashboard.recentFaults')}</h3>
                </div>
                <div className="dash-card-tools">
                  <span className="dash-card-period">{t('dashboard.last30days')}</span>
                  <button type="button" className="dash-table-all" onClick={() => props.history.push('/tasks')}>
                    {t('dashboard.viewAll')} <FaArrowRight />
                  </button>
                </div>
              </div>
              <table>
                <colgroup>
                  <col className="dash-col-date" />
                  <col className="dash-col-site" />
                  <col className="dash-col-type" />
                  <col className="dash-col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('common.date')}</th>
                    <th>{t('common.location')}</th>
                    <th>{t('common.type')}</th>
                    <th>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTaskRows(recentFaults)}
                </tbody>
              </table>
            </div>
          </div>
          <div className="col-lg-6 dash-upcoming">
            <div className="dash-panel dash-table-card">
              <div className="dash-card-head dash-table-header">
                <div className="dash-card-title">
                  <FaTasks />
                  <h3>{t('dashboard.upcomingInspections')}</h3>
                </div>
                <div className="dash-card-tools">
                  <span className="dash-card-period">{t('dashboard.last30days')}</span>
                  <button type="button" className="dash-table-all" onClick={() => props.history.push('/tasks')}>
                    {t('dashboard.viewAll')} <FaArrowRight />
                  </button>
                </div>
              </div>
              <table>
                <colgroup>
                  <col className="dash-col-date" />
                  <col className="dash-col-site" />
                  <col className="dash-col-type" />
                  <col className="dash-col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('common.date')}</th>
                    <th>{t('common.location')}</th>
                    <th>{t('common.type')}</th>
                    <th>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTaskRows(upcomingInspections)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="dash-section dash-analytics">
        <div className="dash-section-heading">
          <h2>{t('dashboard.analyticsTitle')}</h2>
          <p>{t('dashboard.analyticsSubtitle')}</p>
        </div>
        <div className="dash-chart-grid">
          <div className="dash-chart dash-chart-wide">   
            <Graph 
              title={t('dashboard.serviceCalls')}
              subtitle={t('dashboard.last30days')}
              type="bar"
              data={serviceCall}
              options={options}
            />           
          </div>
          <div className="dash-chart">  
            <Graph 
              title={t('dashboard.serviceCallsReactive')}
              subtitle={t('dashboard.last30days')}
              type="bar"
              data={reactiveData}
              options={options}
            />             
          </div>
          <div className="dash-chart">  
            <Graph 
              title={t('dashboard.serviceCallsPpm')}
              subtitle={t('dashboard.last30days')}
              type="bar"
              data={ppmData}
              options={options}
            />              
          </div>
          <div className="dash-chart"> 
            <Graph 
              title={t('dashboard.slaAttendance')}
              subtitle={t('dashboard.last30days')}
              type="bar"
              data={slaAttendanceData}
              options={options}
            />           
          </div>
          <div className="dash-chart">    
            <Graph 
              title={t('dashboard.slaComplete')}
              subtitle={t('dashboard.last30days')}
              type="bar"
              data={slaRepairData}
              options={options}
            />           
          </div>
        </div>
      </section>

        {renderModal(modalType)}
        
    </div>
  );
}


export default Dashboard;
